import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EchoGuest } from '@vtak/shared';
import { UserCheck, X, Volume2, VolumeX, Eye, EyeOff } from 'lucide-react';
import { fetchJson, authHeaders } from '@/services/api-client';
import { CLIENT_CONFIG } from '@/config';

const guestsKey = (joinCode: string) => ['echo', 'guests', joinCode] as const;
const settingsKey = (joinCode: string) => ['echo', 'guest-settings', joinCode] as const;

const missionUrl = (joinCode: string, path: string): string =>
  `${CLIENT_CONFIG.apiBase}/api/echo/missions/${encodeURIComponent(joinCode)}${path}`;

/**
 * Fetch the leader's guest-access state. Throws on 401/403 so the panel can tell
 * "I lead this mission" (200) from "I don't" (error) and hide itself for anyone
 * who isn't the leader — the same authority the server enforces on every action.
 */
async function fetchGuestSettings(joinCode: string): Promise<{ enabled: boolean }> {
  const res = await fetch(missionUrl(joinCode, '/guest-settings'), authHeaders());
  if (!res.ok) throw new Error(`guest-settings ${res.status}`);
  return (await res.json()) as { enabled: boolean };
}

/** Open or close anonymous guest access for the mission. */
async function setGuestAccess(joinCode: string, enabled: boolean): Promise<void> {
  const res = await fetch(
    missionUrl(joinCode, '/guest-settings'),
    authHeaders({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled }) }),
  );
  if (!res.ok) throw new Error(`guest-settings update failed: ${res.status}`);
}

/** POST a per-guest action to the VTAK Echo proxy. */
async function guestAction(joinCode: string, userId: string, action: string): Promise<void> {
  const res = await fetch(
    missionUrl(joinCode, `/guests/${encodeURIComponent(userId)}/${action}`),
    authHeaders({ method: 'POST' }),
  );
  if (!res.ok) throw new Error(`guest ${action} failed: ${res.status}`);
}

/** A small labelled action button. */
function GuestButton({
  onClick,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className="flex h-6 w-6 items-center justify-center rounded bg-white/[0.08] text-muted-foreground hover:text-foreground disabled:opacity-40"
    >
      {children}
    </button>
  );
}

/** One guest row with approve / view / audio / disconnect controls. */
function GuestRow({ guest, joinCode }: { guest: EchoGuest; joinCode: string }): React.JSX.Element {
  const queryClient = useQueryClient();
  const act = useMutation({
    mutationFn: (action: string) => guestAction(joinCode, guest.userId, action),
    onSettled: () => queryClient.invalidateQueries({ queryKey: guestsKey(joinCode) }),
  });

  const pending = guest.status === 'pending';
  return (
    <li className="flex items-center gap-2 rounded-md bg-white/[0.04] px-2.5 py-1.5">
      <span className="flex-1 truncate text-xs text-foreground">{guest.email || guest.userId}</span>
      <span className={`text-[10px] uppercase ${pending ? 'text-amber-400' : 'text-green-400'}`}>{guest.status}</span>
      {pending ? (
        <GuestButton title="Approve as view-only guest" onClick={() => act.mutate('approve')} disabled={act.isPending}>
          <UserCheck className="h-3.5 w-3.5" />
        </GuestButton>
      ) : (
        <>
          <GuestButton
            title={guest.viewGranted ? 'Hide the mission area from this guest' : 'Show the mission area to this guest'}
            onClick={() => act.mutate(guest.viewGranted ? 'revoke-view' : 'grant-view')}
            disabled={act.isPending}
          >
            {guest.viewGranted ? <Eye className="h-3.5 w-3.5 text-echo-yellow" /> : <EyeOff className="h-3.5 w-3.5" />}
          </GuestButton>
          <GuestButton
            title={guest.audioGranted ? 'Revoke mission audio' : 'Let this guest hear Echo'}
            onClick={() => act.mutate(guest.audioGranted ? 'revoke-audio' : 'grant-audio')}
            disabled={act.isPending}
          >
            {guest.audioGranted ? <Volume2 className="h-3.5 w-3.5 text-echo-yellow" /> : <VolumeX className="h-3.5 w-3.5" />}
          </GuestButton>
        </>
      )}
      <GuestButton title="Disconnect guest" onClick={() => act.mutate('disconnect')} disabled={act.isPending}>
        <X className="h-3.5 w-3.5" />
      </GuestButton>
    </li>
  );
}

/** Leader toggle: allow anonymous QR guests to request access to this mission. */
function AccessToggle({ joinCode, enabled }: { joinCode: string; enabled: boolean }): React.JSX.Element {
  const queryClient = useQueryClient();
  const toggle = useMutation({
    mutationFn: () => setGuestAccess(joinCode, !enabled),
    onSettled: () => queryClient.invalidateQueries({ queryKey: settingsKey(joinCode) }),
  });
  return (
    <button
      type="button"
      onClick={() => toggle.mutate()}
      disabled={toggle.isPending}
      className="flex w-full items-center justify-between rounded-md bg-white/[0.04] px-2.5 py-1.5 text-xs text-foreground disabled:opacity-50"
    >
      <span>Allow guest access</span>
      <span
        className={`relative h-4 w-7 rounded-full transition-colors ${enabled ? 'bg-echo-yellow' : 'bg-white/20'}`}
      >
        <span
          className={`absolute top-0.5 h-3 w-3 rounded-full bg-black transition-transform ${enabled ? 'translate-x-3.5' : 'translate-x-0.5'}`}
        />
      </span>
    </button>
  );
}

/**
 * Mission guest manager (leader only). Hidden entirely unless the caller leads
 * the mission (the guest-settings probe 403s everyone else). Lets the leader
 * open/close guest access and, per guest, approve, toggle the mission-area view,
 * grant/revoke listen-only Echo audio, or disconnect them.
 */
export function MissionGuests({ joinCode }: { joinCode: string }): React.JSX.Element | null {
  const settings = useQuery({
    queryKey: settingsKey(joinCode),
    retry: false,
    refetchInterval: 30_000,
    queryFn: () => fetchGuestSettings(joinCode),
  });

  const guests = useQuery({
    queryKey: guestsKey(joinCode),
    enabled: settings.isSuccess,
    refetchInterval: 15_000,
    queryFn: () => fetchJson<EchoGuest[]>(`/api/echo/missions/${encodeURIComponent(joinCode)}/guests`, []),
  });

  // Not the leader (or still probing) → no panel at all.
  if (!settings.isSuccess) return null;

  const list = guests.data ?? [];
  return (
    <div className="space-y-1.5">
      <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Guests</h3>
      <AccessToggle joinCode={joinCode} enabled={settings.data.enabled} />
      {list.length > 0 ? (
        <ul className="space-y-1">
          {list.map((guest) => (
            <GuestRow key={guest.userId} guest={guest} joinCode={joinCode} />
          ))}
        </ul>
      ) : (
        <p className="px-1 text-[10px] text-muted-foreground">
          {settings.data.enabled ? 'No guests yet. Share the mission QR to invite view-only guests.' : 'Turn on guest access to let people join by QR.'}
        </p>
      )}
    </div>
  );
}
